import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  AutoAwesomeRounded,
  EditOutlined,
  ExpandMoreRounded,
  SwapHorizRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { Languages } from "../../../Enums/companyStatus";
import {
  documentCraftingErrorMessage,
  getDocumentCraftingError,
} from "../../../Api/documentCrafting";
import {
  DOCUMENT_LANGUAGES,
  DocumentCraftingCreatePayload,
  DocumentCraftingDuplicatePrefill,
  DocumentCraftingFormField,
  DocumentCraftingInvalidFormError,
  DocumentCraftingLanguage,
  DocumentCraftingRevisionRef,
  DocumentCraftingTypeDefinition,
  DocumentCraftingUpload,
} from "../../../Models/documentCrafting";
import {
  useCreateDocumentCraftingDocument,
  useDocumentCraftingDocument,
  useDocumentCraftingModels,
  useDocumentCraftingMySettings,
  useDocumentCraftingWorkspaceSettings,
} from "../../../Query/useDocumentCrafting";
import { useUser } from "../../../Query/useUsers";
import { useCurrentWorkspace } from "../../../Query/useWorkspace";
import { InfoRow, LoadingState, PageHeader, Panel } from "../Common/DocumentCraftingUi";
import ModelSelect, { resolveDocumentCraftingModelId } from "../Common/ModelSelect";
import { documentCraftingPaths } from "../Common/documentCraftingNavigation";
import {
  uiLanguage,
  useDocumentCraftingEnumLabels,
} from "../Common/useDocumentCraftingEnumLabels";
import {
  FieldStep,
  FormErrors,
  FormValues,
  MAX_FILES_PER_RUN,
  PendingFile,
  PendingFiles,
  PrefillSources,
  WizardStep,
  buildInitialValues,
  collectFileFields,
  countPendingFiles,
  fieldsOfStep,
  altsForRequest,
  filesForRequest,
  groupUploadsByPath,
  initialUploadSelection,
  fileFieldPath,
  isBlankField,
  isKnownPath,
  mapServerErrors,
  normalizeErrorPath,
  selectedUploadIds,
  setAt,
  stepOfPath,
  todayIso,
  trimValues,
  typeRuleErrors,
  validateFields,
  validateText,
  withUploadSelection,
  withoutErrors,
} from "./documentFormModel";
import FormSummary from "./FormSummary";
import SchemaForm, { useFormErrorText } from "./SchemaForm";
import { TypicalCost, usePagesText } from "./TypePicker";

const DUPLICATE_NOTE_MAX = 4000;
const WORKING_TITLE_MAX = 200;
// Inputs of the wizard outside the type schema (server error paths too).
const BASICS_KEYS = ["language", "model_id", "working_title", "duplicate_note", "type"];
// Error key of the per-request file limit (not a field).
const TOTAL_FILES_KEY = "_files";

interface DuplicateContext {
  source: DocumentCraftingRevisionRef;
}

interface FormProps {
  definition: DocumentCraftingTypeDefinition;
  initialValues: FormValues;
  initialLanguage: DocumentCraftingLanguage;
  /** The duplicated revision's model. */
  preferredModelId: number | null;
  /** Earlier uploads per file field path (duplicate). */
  uploadsByPath: Record<string, DocumentCraftingUpload[]>;
  /** Earlier uploads no field fits; copied along unchanged. */
  otherUploadIds: number[];
  duplicate: DuplicateContext | null;
  workspaceMaxMb: number | null;
  onChangeType?: () => void;
}

const SidePanel: React.FC<{
  definition: DocumentCraftingTypeDefinition;
  step: WizardStep;
}> = ({ definition, step }) => {
  const { t } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const pagesText = usePagesText();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Panel title={labels.type(definition.key)}>
        <Typography variant="body2" sx={{ color: color.gray700 }}>
          {labels.typeDescription(definition.key)}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 13, color: color.gray500 }}>
          {[definition.outputs.map((output) => output.toUpperCase()).join(" + "), pagesText(definition)]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <TypicalCost cost={definition.typical_cost} />
        </Box>
      </Panel>
      <Panel title={t(`create.wizard.hints.${step}.title`)}>
        <Typography variant="body2" sx={{ color: color.gray700 }}>
          {t(`create.wizard.hints.${step}.text`)}
        </Typography>
      </Panel>
      <Panel title={t("create.wizard.howItWorks.title")}>
        <Box component="ol" sx={{ m: 0, pl: 2.5, color: color.gray700, fontSize: 14 }}>
          {[1, 2, 3, 4].map((index) => (
            <Box component="li" key={index} sx={{ mb: 0.5 }}>
              {t(`create.wizard.howItWorks.step${index}`)}
            </Box>
          ))}
        </Box>
      </Panel>
    </Box>
  );
};

const DocumentWizardForm: React.FC<FormProps> = ({
  definition,
  initialValues,
  initialLanguage,
  preferredModelId,
  uploadsByPath,
  otherUploadIds,
  duplicate,
  workspaceMaxMb,
  onChangeType,
}) => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const errorText = useFormErrorText();
  const models = useDocumentCraftingModels();
  const createDocument = useCreateDocumentCraftingDocument();
  const sourceDocument = useDocumentCraftingDocument(duplicate?.source.document_id);
  const topRef = useRef<HTMLDivElement>(null);

  const [values, setValues] = useState<FormValues>(initialValues);
  const [files, setFiles] = useState<PendingFiles>({});
  const [language, setLanguage] = useState<DocumentCraftingLanguage>(initialLanguage);
  const [modelId, setModelId] = useState<number | null>(null);
  const [duplicateNote, setDuplicateNote] = useState("");
  const [localTitle, setLocalTitle] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fields: DocumentCraftingFormField[] = definition.fields;
  const fileFields = collectFileFields(fields);
  const hasTitleField = fields.some((field) => field.key === "working_title");
  const stepFields: Record<FieldStep, typeof fields> = {
    basics: fieldsOfStep(fields, "basics"),
    details: fieldsOfStep(fields, "details"),
    files: fieldsOfStep(fields, "files"),
  };
  const steps: WizardStep[] = [
    "basics",
    ...(stepFields.files.length ? (["files"] as const) : []),
    ...(stepFields.details.length ? (["details"] as const) : []),
    "review",
  ];
  // Details the description usually answers: collapsed, optional.
  const structuralDetails = stepFields.details.filter((field) => !field.ask_if_missing);
  const askableDetails = stepFields.details.filter((field) => field.ask_if_missing);
  const step = steps[stepIndex];
  const lastIndex = steps.length - 1;
  const resolvedModelId = resolveDocumentCraftingModelId(models.data, modelId, preferredModelId);
  const busy = createDocument.isPending;

  const scrollTop = () => topRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  const goTo = (index: number) => {
    setStepIndex(index);
    scrollTop();
  };

  const change = (path: string, value: unknown) => {
    setValues((current) => setAt(current, path, value));
    setErrors((current) => withoutErrors(current, path));
  };
  const changeFiles = (path: string, update: (current: PendingFile[]) => PendingFile[]) => {
    setFiles((current) => ({ ...current, [path]: update(current[path] ?? []) }));
    setErrors((current) => withoutErrors(current, path));
  };
  const clearError = (path: string) => setErrors((current) => withoutErrors(current, path));

  const validateStep = (key: WizardStep): FormErrors => {
    if (key === "review") return {};
    const context = { root: values, files, fields, uploads: uploadsByPath };
    const stepErrors = validateFields(stepFields[key], context);
    const ruleErrors = typeRuleErrors(definition.key, values);
    Object.keys(ruleErrors).forEach((path) => {
      if (stepOfPath(path, fields) === key) stepErrors[path] = ruleErrors[path];
    });
    if (key === "basics") {
      if (!language) stepErrors.language = { key: "selectRequired" };
      if (!models.isLoading && resolvedModelId === null) stepErrors.model_id = { key: "modelRequired" };
      if (duplicate) {
        const noteError = validateText(duplicateNote, { required: true, maxLength: DUPLICATE_NOTE_MAX });
        if (noteError) stepErrors.duplicate_note = noteError;
      }
      if (!hasTitleField) {
        const titleError = validateText(localTitle, { maxLength: WORKING_TITLE_MAX });
        if (titleError) stepErrors.working_title = titleError;
      }
    }
    if (key === "files" && countPendingFiles(files) > MAX_FILES_PER_RUN) {
      stepErrors[TOTAL_FILES_KEY] = { key: "tooManyFilesTotal", params: { max: MAX_FILES_PER_RUN } };
    }
    return stepErrors;
  };

  const canOpen = (index: number) =>
    steps.slice(0, index).every((key) => !Object.keys(validateStep(key)).length);

  const next = () => {
    const stepErrors = validateStep(step);
    if (Object.keys(stepErrors).length) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    goTo(stepIndex + 1);
  };

  const showServerErrors = (formErrors: FormErrors) => {
    setErrors(formErrors);
    const indexes = Object.keys(formErrors).map((path) => {
      const fieldStepKey = BASICS_KEYS.includes(path) ? "basics" : stepOfPath(path, fields);
      const index = fieldStepKey ? steps.indexOf(fieldStepKey) : -1;
      return index < 0 ? lastIndex : index;
    });
    goTo(indexes.length ? Math.min(...indexes) : lastIndex);
  };

  const submit = async () => {
    setSubmitError(null);
    for (let index = 0; index < lastIndex; index += 1) {
      const stepErrors = validateStep(steps[index]);
      if (Object.keys(stepErrors).length) {
        setErrors(stepErrors);
        goTo(index);
        return;
      }
    }
    if (resolvedModelId === null) return;

    const workingTitle = (hasTitleField ? String(values.working_title ?? "") : localTitle).trim();
    const payload: DocumentCraftingCreatePayload = {
      type: definition.id,
      language,
      model_id: resolvedModelId,
      values: trimValues(values),
      ...(workingTitle ? { working_title: workingTitle } : {}),
      ...(Object.keys(altsForRequest(files)).length ? { upload_alts: altsForRequest(files) } : {}),
      ...(duplicate
        ? {
            duplicated_from_revision_id: duplicate.source.revision_id,
            duplicate_note: duplicateNote.trim(),
            reuse_upload_ids: [...selectedUploadIds(values, fileFields), ...otherUploadIds],
          }
        : {}),
    };

    try {
      const result = await createDocument.mutateAsync({ payload, files: filesForRequest(files) });
      navigate(documentCraftingPaths.run(result.run_id));
    } catch (error) {
      // The request already showed a toast; put field errors where they belong.
      const body = getDocumentCraftingError(error);
      const filePath =
        typeof body?.field === "string" ? fileFieldPath(normalizeErrorPath(body.field), fields) : null;
      if (body?.code === "invalid_form") {
        showServerErrors(mapServerErrors((body as DocumentCraftingInvalidFormError).errors, fields));
      } else if (body?.code === "model_not_accessible") {
        showServerErrors({
          model_id: { message: documentCraftingErrorMessage(error, t("create.errors.modelRequired")) },
        });
      } else if (filePath && isKnownPath(filePath, fields)) {
        // A rejected upload (file type, size, damaged file) names its field.
        showServerErrors({
          [filePath]: { message: documentCraftingErrorMessage(error, t("create.wizard.submitFailed")) },
        });
      } else {
        setSubmitError(documentCraftingErrorMessage(error, t("create.wizard.submitFailed")));
      }
    }
  };

  const unknownErrors = Object.keys(errors).filter(
    (path) => !BASICS_KEYS.includes(path) && path !== TOTAL_FILES_KEY && !isKnownPath(path, fields),
  );

  const schemaFormProps = {
    schema: fields,
    values,
    errors,
    onChange: change,
    files,
    onFilesChange: changeFiles,
    uploads: uploadsByPath,
    uploadsDocumentId: duplicate?.source.document_id ?? null,
    workspaceMaxMb,
    disabled: busy,
  };

  const renderBasics = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {duplicate && (
        <TextField
          id="dc-field-duplicate_note"
          fullWidth
          size="small"
          multiline
          minRows={3}
          required
          label={t("create.duplicate.noteLabel")}
          placeholder={t("create.duplicate.notePlaceholder")}
          value={duplicateNote}
          disabled={busy}
          onChange={(event) => {
            setDuplicateNote(event.target.value);
            clearError("duplicate_note");
          }}
          inputProps={{ maxLength: DUPLICATE_NOTE_MAX }}
          error={!!errors.duplicate_note}
          helperText={errorText(errors.duplicate_note) ?? t("create.duplicate.noteHelp")}
        />
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <TextField
          id="dc-field-language"
          select
          fullWidth
          size="small"
          required
          label={t("create.wizard.language")}
          value={language}
          disabled={busy}
          onChange={(event) => {
            setLanguage(event.target.value as DocumentCraftingLanguage);
            clearError("language");
          }}
          error={!!errors.language}
          helperText={errorText(errors.language) ?? t("create.wizard.languageHelp")}
        >
          {DOCUMENT_LANGUAGES.map((value) => (
            <MenuItem key={value} value={value}>
              {labels.language(value)}
            </MenuItem>
          ))}
        </TextField>
        {!hasTitleField && (
          <TextField
            id="dc-field-working_title"
            fullWidth
            size="small"
            label={t("create.wizard.workingTitle")}
            value={localTitle}
            disabled={busy}
            onChange={(event) => {
              setLocalTitle(event.target.value);
              clearError("working_title");
            }}
            inputProps={{ maxLength: WORKING_TITLE_MAX }}
            error={!!errors.working_title}
            helperText={errorText(errors.working_title) ?? t("create.wizard.workingTitleHelp")}
          />
        )}
      </Box>
      <SchemaForm fields={stepFields.basics} {...schemaFormProps} />
      <Box id="dc-field-model_id">
        <ModelSelect
          value={modelId}
          onChange={(id) => {
            setModelId(id);
            clearError("model_id");
          }}
          preferredModelId={preferredModelId}
          disabled={busy}
          error={!!errors.model_id}
          helperText={errorText(errors.model_id) ?? t("create.wizard.modelHelp")}
        />
      </Box>
    </Box>
  );

  const renderReview = () => {
    const modelName =
      models.data?.models.find((model) => model.model_id === resolvedModelId)?.model_name ?? "";
    const workingTitle = hasTitleField ? String(values.working_title ?? "") : localTitle;
    const section = (key: FieldStep) =>
      steps.includes(key) && (
        <Box key={key} sx={{ mt: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: color.gray900 }}>
              {t(`create.wizard.steps.${key}`)}
            </Typography>
            <Button
              size="small"
              color="secondary"
              startIcon={<EditOutlined />}
              onClick={() => goTo(steps.indexOf(key))}
              disabled={busy}
            >
              {t("create.summary.edit")}
            </Button>
          </Box>
          {key === "basics" && (
            <>
              {duplicate && <InfoRow label={t("create.duplicate.noteLabel")} value={duplicateNote} />}
              <InfoRow label={t("create.wizard.language")} value={labels.language(language)} />
              {!hasTitleField && <InfoRow label={t("create.wizard.workingTitle")} value={workingTitle} />}
              <InfoRow label={t("common.models.label")} value={modelName} />
            </>
          )}
          <FormSummary fields={stepFields[key]} values={values} files={files} uploads={uploadsByPath} />
        </Box>
      );
    return (
      <>
        <Typography variant="body2" sx={{ color: color.gray700 }}>
          {t("create.wizard.reviewIntro")}
        </Typography>
        {section("basics")}
        {section("files")}
        {section("details")}
        <Alert severity="info" icon={<AutoAwesomeRounded />} sx={{ mt: 3 }}>
          {t("create.wizard.whatHappensNext")}
          {definition.typical_cost !== null && (
            <Box sx={{ mt: 0.5 }}>
              <TypicalCost cost={definition.typical_cost} />
            </Box>
          )}
        </Alert>
      </>
    );
  };

  const renderAskableDetails = () => {
    if (!askableDetails.length) return null;
    const filled = askableDetails.filter(
      (field) => !isBlankField(field, values[field.key], field.key, files),
    ).length;
    const hasErrors = Object.keys(errors).some((path) => stepOfPath(path, askableDetails) !== null);
    return (
      <Accordion
        expanded={detailsOpen || hasErrors}
        onChange={(_, open) => setDetailsOpen(open)}
        disableGutters
        elevation={0}
        sx={{
          mt: structuralDetails.length ? 2.5 : 0,
          border: `1px solid ${color.gray200}`,
          borderRadius: "12px !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: color.gray900 }}>
              {t("create.wizard.detailsMore")}
            </Typography>
            {filled > 0 && (
              <Chip size="small" label={t("create.wizard.detailsFilled", { count: filled })} sx={{ height: 22 }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ color: color.gray700, mb: 2 }}>
            {t("create.wizard.detailsMoreHelp")}
          </Typography>
          <SchemaForm fields={askableDetails} {...schemaFormProps} />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderStep = () => {
    if (step === "basics") return renderBasics();
    if (step === "review") return renderReview();
    if (step === "details") {
      return (
        <>
          {structuralDetails.length > 0 && <SchemaForm fields={structuralDetails} {...schemaFormProps} />}
          {renderAskableDetails()}
        </>
      );
    }
    return (
      <>
        {step === "files" && (
          <Typography variant="body2" sx={{ color: color.gray700, mb: 2 }}>
            {t("create.wizard.filesIntro")}
          </Typography>
        )}
        <SchemaForm fields={stepFields[step]} {...schemaFormProps} />
        {errors[TOTAL_FILES_KEY] && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorText(errors[TOTAL_FILES_KEY])}
          </Alert>
        )}
      </>
    );
  };

  const sourceTitle = sourceDocument.data?.title;
  const title = duplicate
    ? t("create.duplicate.title")
    : t("create.wizard.title", { type: labels.type(definition.key) });
  const subtitle = duplicate
    ? sourceTitle
      ? t("create.duplicate.subtitle", { title: sourceTitle, number: duplicate.source.revision_number })
      : t("create.duplicate.subtitleNoTitle", { number: duplicate.source.revision_number })
    : labels.typeDescription(definition.key);

  return (
    <>
      <Box ref={topRef} sx={{ scrollMarginTop: 16 }} />
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {onChangeType && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SwapHorizRounded />}
                onClick={onChangeType}
                disabled={busy}
              >
                {t("create.wizard.changeType")}
              </Button>
            )}
            <Button
              color="secondary"
              onClick={() => navigate(documentCraftingPaths.list)}
              disabled={busy}
            >
              {t("common.cancel")}
            </Button>
          </>
        }
      />

      <Box sx={{ mb: 3 }}>
        <Panel sx={{ py: 2 }}>
          <Stepper nonLinear activeStep={stepIndex} alternativeLabel>
            {steps.map((key, index) => (
              <Step key={key} completed={index < stepIndex && canOpen(index + 1)}>
                <StepButton
                  onClick={() => goTo(index)}
                  disabled={busy || index === stepIndex || !canOpen(index)}
                >
                  <StepLabel>{t(`create.wizard.steps.${key}`)}</StepLabel>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Panel>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 320px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Panel
          title={t("create.wizard.stepTitle", {
            number: stepIndex + 1,
            title: t(`create.wizard.steps.${step}`),
          })}
        >
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("create.wizard.errorSummary")}
              {unknownErrors.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {unknownErrors.map((path) => (
                    <li key={path}>{`${path}: ${errorText(errors[path])}`}</li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
          {renderStep()}
          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${color.gray100}`,
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: 12, color: color.gray500 }}>
              {t("create.wizard.requiredHint")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {stepIndex > 0 && (
                <Button
                  color="secondary"
                  startIcon={<ArrowBackRounded />}
                  onClick={() => goTo(stepIndex - 1)}
                  disabled={busy}
                >
                  {t("common.back")}
                </Button>
              )}
              {stepIndex < lastIndex ? (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardRounded />}
                  onClick={next}
                  disabled={busy}
                >
                  {t("create.wizard.nextTo", { step: t(`create.wizard.steps.${steps[stepIndex + 1]}`) })}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeRounded />}
                  onClick={submit}
                  disabled={busy || models.isLoading}
                >
                  {busy ? t("create.wizard.submitting") : t("create.wizard.submit")}
                </Button>
              )}
            </Box>
          </Box>
        </Panel>

        <SidePanel definition={definition} step={step} />
      </Box>
    </>
  );
};

interface Props {
  definition: DocumentCraftingTypeDefinition;
  /** "Duplicate as new document": the prefilled form of a saved revision. */
  duplicate?: DocumentCraftingDuplicatePrefill | null;
  onChangeType?: () => void;
}

/**
 * The create form of one document type: waits for the prefill sources (the
 * user, the remembered contact data, the workspace defaults), then mounts
 * the wizard once with its initial values (no effect copies data in).
 */
const DocumentWizard: React.FC<Props> = ({ definition, duplicate, onChangeType }) => {
  const { i18n } = useTranslation("documentCrafting");
  const user = useUser();
  const settings = useDocumentCraftingMySettings();
  const workspaceSettings = useDocumentCraftingWorkspaceSettings();
  const workspace = useCurrentWorkspace();
  const [today] = useState(() => todayIso());

  if ([user, settings, workspaceSettings, workspace].some((query) => query.isLoading)) {
    return <LoadingState />;
  }

  const sources: PrefillSources = {
    user: user.data ?? null,
    settings: settings.data ?? null,
    workspace: workspaceSettings.data ?? null,
    today,
  };
  // SPEC §5.1: the workspace language decides (German → de, otherwise en).
  const workspaceLanguage = workspace.data?.workspace?.language;
  const defaultLanguage: DocumentCraftingLanguage =
    workspaceLanguage === undefined
      ? uiLanguage(i18n.language)
      : workspaceLanguage === Languages.GERMAN
        ? "de"
        : "en";

  const fileFields = collectFileFields(definition.fields);
  let initialValues = buildInitialValues(
    definition.fields,
    sources,
    duplicate ? duplicate.prefill.values : null,
  );
  let uploadsByPath: Record<string, DocumentCraftingUpload[]> = {};
  let otherUploadIds: number[] = [];
  if (duplicate) {
    const selection = initialUploadSelection(fileFields, duplicate.prefill.uploads, null);
    initialValues = withUploadSelection(initialValues, selection.byPath);
    uploadsByPath = groupUploadsByPath(duplicate.prefill.uploads, fileFields);
    otherUploadIds = selection.other;
  }

  const key = duplicate
    ? `duplicate-${duplicate.source.document_id}-${duplicate.source.revision_id}`
    : `new-${definition.key}`;

  return (
    <DocumentWizardForm
      key={key}
      definition={definition}
      initialValues={initialValues}
      initialLanguage={duplicate?.prefill.language ?? defaultLanguage}
      preferredModelId={duplicate?.prefill.model_id ?? null}
      uploadsByPath={uploadsByPath}
      otherUploadIds={otherUploadIds}
      duplicate={duplicate ? { source: duplicate.source } : null}
      workspaceMaxMb={workspaceSettings.data?.max_upload_mb ?? null}
      onChangeType={onChangeType}
    />
  );
};

export default DocumentWizard;
